/**
 * ApplicationTab
 *
 * Drawer tab that surfaces the application-focused view for an :Asset
 * observation that belongs to a parent :Application rollup root. Shows:
 *
 *   - Parent identity (FQDN + key)
 *   - All sibling members of the same Application
 *   - Risks linked to any of those members
 *
 * One relationship query: from the :Application keyed by the current
 * asset's `group_key`, walk outgoing HAS_MEMBER to all members, and
 * (optionally) outgoing HAS_VULNERABILITY to each member's risks.
 *
 * The tree response gives us per-member relationship data so we can
 * group risks by their owning member in the UI without a second hop.
 */
import React, { useMemo } from 'react';

import { Loader } from '@/components/Loader';
import { useMy } from '@/hooks/useMy';
import { applicationRollupKey } from '@/sections/asset/utils';
import type { Asset, Risk, TreeNode } from '@/types';
import { flattenAssetGraphResponse } from '@/utils/api/flattenAssetGraphResponse';

interface ApplicationTabProps {
  /**
   * The observation whose drawer the user opened. Its `group_key`
   * identifies the parent Application this tab queries.
   */
  asset: Asset;
}

interface MemberWithRisks {
  member: Asset;
  risks: Risk[];
}

export const ApplicationTab: React.FC<ApplicationTabProps> = ({ asset }) => {
  // The :Application key to query. Resolves to the asset's own key when the
  // drawer is already showing the parent :Application (the resolve-up path,
  // where the re-fetched parent carries no `group_key`) and to `group_key`
  // when a member observation is opened directly. See applicationRollupKey.
  const appKey = applicationRollupKey(asset);

  // One relationship query — find the parent :Application by its key
  // and walk to every member, with HAS_VULNERABILITY optionally walked
  // to each member's Risks. Tree result preserves the parent/child
  // structure so we can group risks by their owning member below.
  const { data, status } = useMy(
    {
      resource: 'asset',
      tree: true,
      query: {
        node: {
          labels: ['Application'],
          filters: [{ field: 'key', operator: '=', value: appKey || '' }],
          relationships: [
            {
              label: 'HAS_MEMBER',
              target: {
                labels: ['Asset'],
                relationships: [
                  {
                    label: 'HAS_VULNERABILITY',
                    optional: true,
                    target: { labels: ['Risk'] },
                  },
                ],
              },
            },
          ],
        },
      },
    },
    { enabled: Boolean(appKey) }
  );

  const { membersWithRisks, parent } = useMemo(() => {
    const out: MemberWithRisks[] = [];
    if (!data?.length) return { membersWithRisks: out, parent: undefined };

    // The first TreeNode IS the Application (the primary node of the
    // query). Its relationships[] are HAS_MEMBER edges to members. Each
    // member's relationships[] are HAS_VULNERABILITY edges to risks.
    const root = data[0] as TreeNode | undefined;
    const result = root?.node?.result as Record<string, unknown[]> | undefined;
    const apps = (result?.applications as Asset[] | undefined) ?? [];
    const parentApp = apps[0];

    const memberRels = root?.node?.relationships ?? [];
    for (const memberRel of memberRels) {
      const memberResult = memberRel.target?.result;
      // Members come back bucketed by asset subtype. Reuse the canonical
      // flattener rather than re-listing the buckets here — the previous
      // hand-rolled list omitted `addomains`, silently dropping every AD
      // member (and would drift again on the next subtype). One member node
      // per HAS_MEMBER edge, so take the representative.
      const member = flattenAssetGraphResponse(memberResult)[0];
      if (!member) continue;

      const risks: Risk[] = [];
      const memberSubRels =
        // TreeNode types don't promise this depth; cast via unknown.
        (memberRel.target as unknown as TreeNode['node'])?.relationships ?? [];
      for (const riskRel of memberSubRels) {
        const riskResult = riskRel.target?.result as
          | Record<string, unknown[]>
          | undefined;
        const matched = (riskResult?.risks as Risk[] | undefined) ?? [];
        risks.push(...matched);
      }
      out.push({ member, risks });
    }
    return { membersWithRisks: out, parent: parentApp };
  }, [data]);

  if (!appKey) {
    return (
      <div className="text-default-light p-6">
        This observation isn&rsquo;t grouped under an Application.
      </div>
    );
  }

  // Prefer the FQDN from the queried :Application node itself (reroute-safe
  // and already lowercased on the backend) over the opened observation's
  // `group` — which is the member's own DNS when a member is opened directly.
  // Matches ApplicationCell's source-of-truth ordering. See item A / I3.
  const parentFqdn = parent?.fqdn || parent?.name;
  const totalRisks = membersWithRisks.reduce((n, m) => n + m.risks.length, 0);

  return (
    <Loader isLoading={status === 'pending'} className="m-6">
      <div className="space-y-6 p-6">
        <section>
          <h2 className="text-lg font-semibold">
            {parentFqdn || parent?.name || 'Application'}
          </h2>
          <div className="text-default-light mt-1 text-sm">
            {membersWithRisks.length} member
            {membersWithRisks.length === 1 ? '' : 's'}
            {' · '}
            {totalRisks} risk{totalRisks === 1 ? '' : 's'}
          </div>
        </section>

        <section>
          <h3 className="text-default-light text-sm font-semibold tracking-wide uppercase">
            Members
          </h3>
          <ul className="divide-default-light/30 mt-2 divide-y">
            {membersWithRisks.map(({ member, risks }) => (
              <li
                key={member.key}
                className="flex items-center justify-between py-2 text-sm"
              >
                <span className="truncate" title={member.identifier}>
                  {member.name || member.identifier}
                </span>
                <span className="text-default-light ml-4 shrink-0">
                  {risks.length} risk{risks.length === 1 ? '' : 's'}
                </span>
              </li>
            ))}
            {membersWithRisks.length === 0 && (
              <li className="text-default-light py-2 text-sm">
                No members found.
              </li>
            )}
          </ul>
        </section>

        {totalRisks > 0 && (
          <section>
            <h3 className="text-default-light text-sm font-semibold tracking-wide uppercase">
              Risks across all members
            </h3>
            <ul className="divide-default-light/30 mt-2 divide-y">
              {membersWithRisks.flatMap(({ member, risks }) =>
                risks.map(risk => (
                  <li
                    key={`${member.key}::${risk.key}`}
                    className="py-2 text-sm"
                  >
                    <div className="font-medium">{risk.name || risk.key}</div>
                    <div className="text-default-light text-xs">
                      on {member.name || member.identifier}
                    </div>
                  </li>
                ))
              )}
            </ul>
          </section>
        )}
      </div>
    </Loader>
  );
};
